import React, {useCallback, useEffect, useRef, useState} from 'react'
import {createPortal} from 'react-dom'

import {$isCodeHighlightNode, $isCodeNode, CODE_LANGUAGE_MAP} from '@lexical/code'
import {$isLinkNode} from '@lexical/link'
import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext'
import {$findMatchingParent, $getNearestNodeOfType, mergeRegister} from '@lexical/utils'
import {
    $getSelection, $isElementNode,
    $isParagraphNode,
    $isRangeSelection, $isRootOrShadowRoot,
    $isTextNode,
    COMMAND_PRIORITY_LOW, ElementFormatType,
    FORMAT_TEXT_COMMAND,
    LexicalEditor,
    SELECTION_CHANGE_COMMAND,
} from 'lexical';

import {getDOMRangeRect} from '../../utils/getDOMRangeRect'
import {getSelectedNode} from '../../utils/getSelectedNode'
import {setFloatingElemPosition} from '../../utils/setFloatingElemPosition'
import ControlButtonStandard from "../../../buttons/control-button/control-button-standard"
import {lexicalInsertLink} from "../../actions/insert-link"
import ColorToolbarSelector from "../toolbar/blocks/combined/color-selector/color-selector"
import {$isFontColorNode, FontColorType} from "../highlight/font-color/font-color-plugin"
import {$isBadgeNode, BadgeType} from "../highlight/badge/badge-plugin"
import {BadgeNode} from "../highlight/badge/node/badge-node"
import {FontColorNode} from "../highlight/font-color/node/font-color-node"
import {lexicalClearFormatting} from "../../actions/clear-formatting"
import {lexicalInsertMentionTrigger} from "../../actions/insert-mention-trigger"
import AlignToolbar from "../toolbar/blocks/align/align"
import ListToolbar from "../toolbar/blocks/list/list"
import {blockTypeToBlockName} from "../toolbar/toolbar"
import {$isListNode, ListNode} from "@lexical/list"
import {$isHeadingNode} from "@lexical/rich-text"
import {$isParentElementRTL} from "@lexical/selection"
import HeadingToolbar from "../toolbar/blocks/heading/heading";
import lexical_convertFromMarkdown from "../../actions/convert-from-markdown"

type Props = {
    editor          : LexicalEditor
    anchorElem      : HTMLElement
    isBold          : boolean
    isCode          : boolean
    isItalic        : boolean
    isLink          : boolean
    isStrikethrough : boolean
    isSubscript     : boolean
    isSuperscript   : boolean
    isUnderline     : boolean

    fontColorType?  : FontColorType | null
    badgeType?      : BadgeType | null
    blockType       : null | string
    isRTL           : boolean
    elementFormat   : ElementFormatType
}

function TextFormatFloatingToolbar(props:Props): JSX.Element {
    const popupCharStylesEditorRef = useRef<HTMLDivElement | null>(null);

    function mouseMoveListener(e: MouseEvent) {
        if (
            popupCharStylesEditorRef?.current &&
            (e.buttons === 1 || e.buttons === 3)
        ) {
            if (popupCharStylesEditorRef.current.style.pointerEvents !== 'none') {
                const x = e.clientX
                const y = e.clientY
                const elementUnderMouse = document.elementFromPoint(x, y);

                if (!popupCharStylesEditorRef.current.contains(elementUnderMouse)) {
                    // Mouse is not over the target element => not a normal click, but probably a drag
                    popupCharStylesEditorRef.current.style.pointerEvents = 'none'
                }
            }
        }
    }

    function mouseUpListener(e: MouseEvent) {
        if (popupCharStylesEditorRef?.current) {
            if (popupCharStylesEditorRef.current.style.pointerEvents !== 'all') {
                popupCharStylesEditorRef.current.style.pointerEvents = 'all'
            }
        }
    }

    useEffect(() => {
        if (popupCharStylesEditorRef?.current) {
            document.addEventListener('mousemove', mouseMoveListener)
            document.addEventListener('mouseup', mouseUpListener, true)

            return () => {
                document.removeEventListener('mousemove', mouseMoveListener)
                document.removeEventListener('mouseup', mouseUpListener, true)
            }
        }
    }, [popupCharStylesEditorRef])

    const $updateTextFormatFloatingToolbar = useCallback(() => {
        const selection = $getSelection()

        const popupCharStylesEditorElem = popupCharStylesEditorRef.current
        const nativeSelection = window.getSelection()

        if (popupCharStylesEditorElem === null) {
            return
        }

        const rootElement = props.editor.getRootElement()
        if (
            selection !== null &&
            nativeSelection !== null &&
            !nativeSelection.isCollapsed &&
            rootElement !== null &&
            rootElement.contains(nativeSelection.anchorNode)
        ) {
            const rangeRect = getDOMRangeRect(nativeSelection, rootElement);

            setFloatingElemPosition(
                rangeRect,
                popupCharStylesEditorElem,
                props.anchorElem,
                props.isLink,
                15 // for shadow not to be cropped if toolbar is at bottom
            );
        }
    }, [props.editor, props.anchorElem, props.isLink])

    useEffect(() => {
        const scrollerElem = props.anchorElem.parentElement

        const update = () => {
            props.editor.getEditorState().read(() => {
                $updateTextFormatFloatingToolbar()
            });
        };

        window.addEventListener('resize', update)
        if (scrollerElem) {
            scrollerElem.addEventListener('scroll', update)
        }

        return () => {
            window.removeEventListener('resize', update)
            if (scrollerElem) {
                scrollerElem.removeEventListener('scroll', update)
            }
        };
    }, [props.editor, $updateTextFormatFloatingToolbar, props.anchorElem])

    useEffect(() => {
        props.editor.getEditorState().read(() => {
            $updateTextFormatFloatingToolbar()
        })
        return mergeRegister(
            props.editor.registerUpdateListener(({editorState}) => {
                editorState.read(() => {
                    $updateTextFormatFloatingToolbar()
                })
            }),

            props.editor.registerCommand(
                SELECTION_CHANGE_COMMAND,
                () => {
                    $updateTextFormatFloatingToolbar()
                    return false;
                },
                COMMAND_PRIORITY_LOW,
            ),
        )
    }, [props.editor, $updateTextFormatFloatingToolbar])

    // is required to prevent toolbar hide before button click
    const onMouseDown = (e: any) => {
        e.preventDefault()
        e.stopPropagation()
    }

    const onClickBold                   = () => props.editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold')
    const onClickItalic                 = () => props.editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic')
    const onClickUnderline              = () => props.editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline')
    const onClickStrikethrough          = () => props.editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough')
    const onClickSubscript              = () => props.editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'subscript')
    const onClickSuperscript            = () => props.editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'superscript')
    const onClickCode                   = () => props.editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'code')
    const onClickMention                = () => {lexicalInsertMentionTrigger(props.editor)}
    const onClickLink                   = () => lexicalInsertLink(props.editor)
    const onClickClearFormatting        = () => lexicalClearFormatting(props.editor)

    return (
        <div ref={popupCharStylesEditorRef}
             className="floating-text-format-popup gd-draftail-toolbar"
             onMouseDown={onMouseDown}>
            {props.editor.isEditable() &&
                <>
                    <HeadingToolbar editor      = {props.editor}
                                    blockType   = {props.blockType}/>

                    <div className="rte-toolbar-separator"/>

                    <ControlButtonStandard  iconClass   = "editor-i5-bold fs-16"
                                            isSelected  = {props.isBold}
                                            onClick     = {onClickBold}/>

                    <ControlButtonStandard  iconClass   = "editor-i5-italic fs-16"
                                            isSelected  = {props.isItalic}
                                            onClick     = {onClickItalic}/>

                    <ColorToolbarSelector editor        = {props.editor}
                                          fontColorType = {props.fontColorType}
                                          badgeType     = {props.badgeType}/>

                    <ControlButtonStandard  iconClass   = "editor-i5-underline fs-16"
                                            isSelected  = {props.isUnderline}
                                            onClick     = {onClickUnderline}/>

                    <ControlButtonStandard  iconClass   = "editor-i5-strikethrough fs-16"
                                            isSelected  = {props.isStrikethrough}
                                            onClick     = {onClickStrikethrough}/>

                    <ControlButtonStandard  iconClass   = "editor-i5-subscript fs-16"
                                            isSelected  = {props.isSubscript}
                                            onClick     = {onClickSubscript}/>

                    <ControlButtonStandard  iconClass   = "editor-i5-superscript fs-16"
                                            isSelected  = {props.isSuperscript}
                                            onClick     = {onClickSuperscript}/>

                    <ControlButtonStandard  iconClass   = "editor-i5-code fs-16"
                                            isSelected  = {props.isCode}
                                            onClick     = {onClickCode}/>

                    <div className="rte-toolbar-separator"/>

                    {/*<ControlButtonStandard  iconClass   = "editor-i5-mention fs-16"
                                            onClick     = {onClickMention}/>*/}

                    <ControlButtonStandard  iconClass   = "editor-i5-link fs-16"
                                            isSelected  = {props.isLink}
                                            onClick     = {onClickLink}/>

                    <div className="rte-toolbar-separator"/>

                    <AlignToolbar editor={props.editor}
                                  elementFormat={props.elementFormat}
                                  isRTL={props.isRTL}
                                  blockType={props.blockType}/>

                    <ListToolbar editor={props.editor}
                                 blockType={props.blockType}/>

                    <div className="rte-toolbar-separator"/>

                    <ControlButtonStandard  iconClass   = "i5-none fs-16"
                                            onClick     = {onClickClearFormatting}/>
                </>}
        </div>
    )
}

function useFloatingTextFormatToolbar(
    editor              : LexicalEditor,
    anchorElem          : HTMLElement,
): JSX.Element | null {
    const [blockType, setBlockType]             = useState<keyof typeof blockTypeToBlockName>('custom-paragraph')
    const [badgeType, setBadgeType]             = useState<BadgeType | null>(null)
    const [fontColorType, setFontColorType]     = useState<FontColorType | null>(null)
    const [elementFormat, setElementFormat]     = useState<ElementFormatType>('left')
    const [isText, setIsText]                   = useState(false)
    const [isLink, setIsLink]                   = useState(false)
    const [isBold, setIsBold]                   = useState(false)
    const [isItalic, setIsItalic]               = useState(false)
    const [isUnderline, setIsUnderline]         = useState(false)
    const [isStrikethrough, setIsStrikethrough] = useState(false)
    const [isSubscript, setIsSubscript]         = useState(false)
    const [isSuperscript, setIsSuperscript]     = useState(false)
    const [isCode, setIsCode]                   = useState(false)
    const [isRTL, setIsRTL]                     = useState(false)

    const updatePopup = useCallback(() => {
        editor.getEditorState().read(() => {
            // Should not to pop up the floating toolbar when using IME input
            if (editor.isComposing()) {
                return
            }
            const selection = $getSelection()
            const nativeSelection = window.getSelection()
            const rootElement = editor.getRootElement()

            if (
                nativeSelection !== null &&
                (!$isRangeSelection(selection) ||
                    rootElement === null ||
                    !rootElement.contains(nativeSelection.anchorNode))
            ) {
                setIsText(false)
                return
            }

            if (!$isRangeSelection(selection)) {
                return
            }

            const node = getSelectedNode(selection)

            const anchorNode = selection.anchor.getNode();
            let element =
                anchorNode.getKey() === 'root'
                    ? anchorNode
                    : $findMatchingParent(anchorNode, (e) => {
                        const parent = e.getParent()
                        return parent !== null && $isRootOrShadowRoot(parent);
                    });

            if (element === null) {
                element = anchorNode.getTopLevelElementOrThrow()
            }

            const elementKey = element.getKey()
            const elementDOM = editor.getElementByKey(elementKey)

            // Update text format
            setIsBold(selection.hasFormat('bold'))
            setIsItalic(selection.hasFormat('italic'))
            setIsUnderline(selection.hasFormat('underline'))
            setIsStrikethrough(selection.hasFormat('strikethrough'))
            setIsSubscript(selection.hasFormat('subscript'))
            setIsSuperscript(selection.hasFormat('superscript'))
            setIsCode(selection.hasFormat('code'))
            setIsRTL($isParentElementRTL(selection))

            // Update links
            const parent = node.getParent()
            if ($isLinkNode(parent) || $isLinkNode(node)) {
                setIsLink(true)
            } else {
                setIsLink(false)
            }

            //update badges
            let badgeNode: BadgeNode | undefined
            if ($isBadgeNode(node))                 badgeNode = node
            if (!badgeNode && $isBadgeNode(parent)) badgeNode = parent
            if (!!badgeNode) {
                setBadgeType(badgeNode.getType())
            } else {
                setBadgeType(null)
            }

            //update font color
            let fontColorNode: FontColorNode | undefined
            if ($isFontColorNode(node))                     fontColorNode = node
            if (!fontColorNode && $isFontColorNode(parent)) fontColorNode = parent
            if (!!fontColorNode) {
                setFontColorType(fontColorNode.getType())
            } else {
                setFontColorType(null)
            }

            if (elementDOM !== null) {
                // setSelectedElementKey(elementKey);
                if ($isListNode(element)) {
                    const parentList = $getNearestNodeOfType<ListNode>(
                        anchorNode,
                        ListNode,
                    );
                    const type = parentList
                        ? parentList.getListType()
                        : element.getListType();
                    setBlockType(type);
                } else {
                    const type = $isHeadingNode(element)
                        ? element.getTag()
                        : element.getType();
                    if (type in blockTypeToBlockName) {
                        setBlockType(type as keyof typeof blockTypeToBlockName);
                    }
                    if ($isCodeNode(element)) {
                        const language =
                            element.getLanguage() as keyof typeof CODE_LANGUAGE_MAP;
                        /*setCodeLanguage(
                            language ? CODE_LANGUAGE_MAP[language] || language : '',
                        );*/
                        return;
                    }
                }
            }

            if (
                !$isCodeHighlightNode(selection.anchor.getNode()) &&
                selection.getTextContent() !== ''
            ) {
                setIsText($isTextNode(node) || $isParagraphNode(node))
            } else {
                setIsText(false)
            }

            const rawTextContent = selection.getTextContent().replace(/\n/g, '')
            if (!selection.isCollapsed() && rawTextContent === '') {
                setIsText(false)
                return
            }

            setElementFormat(
                ($isElementNode(node)
                    ? node.getFormatType()
                    : parent?.getFormatType()) || 'left',
            );
        })
    }, [editor])

    useEffect(() => {
        document.addEventListener('selectionchange', updatePopup)
        return () => {
            document.removeEventListener('selectionchange', updatePopup)
        }
    }, [updatePopup])

    useEffect(() => {
        return mergeRegister(
            editor.registerUpdateListener(() => {
                updatePopup();
            }),
            editor.registerRootListener(() => {
                if (editor.getRootElement() === null) {
                    setIsText(false);
                }
            }),
        )
    }, [editor, updatePopup]);

    if (!isText) {
        return null
    }

    return createPortal(
        <TextFormatFloatingToolbar editor           = {editor}
                                   anchorElem       = {anchorElem}
                                   isLink           = {isLink}
                                   isBold           = {isBold}
                                   isItalic         = {isItalic}
                                   isStrikethrough  = {isStrikethrough}
                                   isSubscript      = {isSubscript}
                                   isSuperscript    = {isSuperscript}
                                   isUnderline      = {isUnderline}
                                   isCode           = {isCode}
                                   fontColorType    = {fontColorType}
                                   badgeType        = {badgeType}
                                   blockType        = {blockType}
                                   isRTL            = {isRTL}
                                   elementFormat    = {elementFormat}/>,
        anchorElem,
    )
}

export default function FloatingTextFormatToolbarPlugin({
                                                            anchorElem = document.body,
                                                        }: {
    anchorElem?         : HTMLElement
}): JSX.Element | null {
    const [editor] = useLexicalComposerContext()
    return useFloatingTextFormatToolbar(editor, anchorElem)
}
